import { useMutation, useQueryClient } from "@tanstack/react-query";
import uuid from "uuid";

import {
    ControlConclusionAnswerKind,
    ControlConclusionFieldKind,
    SampleConclusionAnswerKind,
    SampleConclusionFieldKind,
    CreatableTestingProgramBody,
} from "api/generated/model";
import {
    getGetTestingProgramsQueryKey,
    postTestingPrograms,
} from "api/generated/testing-programs/testing-programs";
import { FieldCondition, FieldGroup } from "components/fieldBuilder/models";

import {
    ControlConclusionField,
    ControlConclusionFieldArray,
    ControlConclusionFieldType,
    SampleConclusionField,
    SampleConclusionFieldArray,
} from "../components/ControlConclusionBuilder/schema";
import { TestingProgramFormSchema } from "../useTestingProgramViewFormSchema";
import {
    controlConclusionFormFieldToApiFieldCondition,
    sampleConclusionFormFieldToApiFieldCondition,
} from "../utils";

interface CreateTestingControlMutationVars {
    data: TestingProgramFormSchema;
}
export function useCreateTestingProgramMutation() {
    const client = useQueryClient();
    return useMutation({
        mutationFn: (vars: CreateTestingControlMutationVars) => {
            const transformedData = toCreatableTestingProgramBody(vars.data);
            return postTestingPrograms(transformedData);
        },
        onSuccess: () =>
            client.invalidateQueries({
                queryKey: getGetTestingProgramsQueryKey(),
            }),
    });
}
function toControlConclusionFieldKind(
    fieldType: ControlConclusionFieldType
): ControlConclusionFieldKind {
    if (fieldType.type === "Effectiveness") {
        return {
            type: "Effectiveness",
            effectiveOptionName: fieldType.options.effectiveOptionName,
            partiallyEffectiveOptionName:
                fieldType.options.partiallyEffectiveOptionName,
            notEffectiveOptionName: fieldType.options.notEffectiveOptionName,
        };
    }
    return fieldType;
}

function toControlConclusionFieldGroup(
    fields: ControlConclusionFieldArray
): FieldGroup<ControlConclusionFieldKind, ControlConclusionAnswerKind> {
    const findTargetField = (
        fieldId: string
    ): ControlConclusionField | undefined => {
        return fields.find((f) => f.id === fieldId);
    };
    return {
        id: uuid.v4(),
        fields: fields.map((field) => {
            let condition: FieldCondition<ControlConclusionAnswerKind> | null =
                null;
            if (field.condition !== null) {
                const targetField = findTargetField(field.condition.fieldId);
                if (targetField === undefined) {
                    throw new Error("Condition on a field that does not exist");
                }
                condition = {
                    ...field.condition,
                    value: controlConclusionFormFieldToApiFieldCondition(
                        targetField,
                        field.condition.value
                    ),
                };
            }
            return {
                ...field,
                condition,
                fieldType: toControlConclusionFieldKind(field.fieldType),
            };
        }),
    };
}

function toSampleConclusionFieldGroup(
    fields: SampleConclusionFieldArray
): FieldGroup<SampleConclusionFieldKind, SampleConclusionAnswerKind> {
    const findTargetField = (
        fieldId: string
    ): SampleConclusionField | undefined => {
        return fields.find((f) => f.id === fieldId);
    };
    return {
        id: uuid.v4(),
        fields: fields.map((field) => {
            let condition: FieldCondition<SampleConclusionAnswerKind> | null =
                null;
            if (field.condition !== null) {
                const targetField = findTargetField(field.condition.fieldId);
                if (targetField === undefined) {
                    throw new Error("Condition on a field that does not exist");
                }
                condition = {
                    ...field.condition,
                    value: sampleConclusionFormFieldToApiFieldCondition(
                        targetField,
                        field.condition.value
                    ),
                };
            }
            return {
                ...field,
                condition,
                fieldType: field.fieldType,
            };
        }),
    };
}

function toCreatableTestingProgramBody(
    data: TestingProgramFormSchema
): CreatableTestingProgramBody {
    return {
        name: data.name,
        description: data.description,
        userIdAndRights: data.accessRights,
        controlConclusionFieldGroup: toControlConclusionFieldGroup(
            data.controlConclusionTasks
        ),
        sampleConclusionFieldGroup: toSampleConclusionFieldGroup(
            data.sampleConclusionTasks
        ),
    };
}
